import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, Subscription, tap } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { User, UserType } from '../../../models/User';
import { UserService } from '../../../services/user.service';
import { FormControlService } from '../../../services/form-control.service';
import { DynamicForm } from '../../../models/DynamicForm';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../../../services/utils.service';
import { LocaleService } from '../../../services/locale.service';
import { ICD10 } from '../../../models/ICD10';
import { HealthRecordService } from '../../../services/healthrecord.service';
import { UserAvailabilityComponent } from '../user-availability/user-availability.component';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { Tab, TabList, TabPanel, Tabs } from 'primeng/tabs';
import { DynamicFormComponent } from '../../dynamic-form/dynamic-form/dynamic-form.component';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ButtonDirective } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { SelectButton } from 'primeng/selectbutton';
import { Tooltip } from 'primeng/tooltip';
import {
  Icd10DisplayLanguage,
  defaultIcd10DisplayLanguage,
  icd10NameColumnHeaderKey,
  icd10ShowShortNameColumn,
  isIcd10Deprecated,
  pickIcd10DisplayName,
  pickIcd10LongName
} from '../../../utils/icd10-display.util';
import { AppointmentPeriodScheduleComponent } from '../appointment-period/appointment-period-schedule/appointment-period-schedule.component';
import { OrganizationUnitService } from 'src/app/services/organization-unit.service';
import { VisibleOnlyDirective } from 'src/app/directives/visible-only.directive';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],

  imports: [
    PageTitleComponent,
    TranslatePipe,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    DynamicFormComponent,
    AsyncPipe,
    UserAvailabilityComponent,
    FormsModule,
    InputText,
    TableModule,
    ButtonDirective,
    ConfirmDialog,
    SelectButton,
    Tooltip,
    AppointmentPeriodScheduleComponent,
    VisibleOnlyDirective
  ]
})
export class ProfileComponent implements OnInit, OnDestroy {
  private formName = 'profile';
  private userProfileSubscription$: Subscription | null;
  public diagnosis: Array<ICD10>;
  public userDiagnosis: Array<ICD10>;
  //selectedUserDiagnosis!: ICD10[] | null;
  public selectedUserDiagnosis: Array<ICD10>;

  public user: User | null;
  public userType = UserType.staff;
  public activeTab = '0';
  public profileForm$: Observable<DynamicForm> | null = null;
  public filterType: string = 'service';
  loading: boolean = false;
  userDiagnosisLoading: boolean = false;
  totalRecords!: number;
  userDiagnosisTotalRecords!: number;
  products!: ICD10[];
  product!: ICD10;
  selectedProducts!: ICD10[] | null;

  // FI/SV/EN/LT long-name display switch for the two ICD-10 tables below.
  displayLanguageOptions: { label: string; value: Icd10DisplayLanguage }[] = [
    { label: 'FI', value: 'fi' },
    { label: 'SV', value: 'sv' },
    { label: 'EN', value: 'en' },
    { label: 'LT', value: 'lt' }
  ];
  displayLanguage: Icd10DisplayLanguage = 'fi';

  @ViewChild(UserAvailabilityComponent)
  userAvailabilityComponent!: UserAvailabilityComponent;

  @ViewChild(AppointmentPeriodScheduleComponent)
  appointmentPeriodScheduleComponent!: AppointmentPeriodScheduleComponent;

  constructor(
    private formService: FormControlService,
    private authService: AuthService,
    private translateService: TranslateService,
    private localeService: LocaleService,
    private userService: UserService,
    private utilsService: UtilsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private healthRecordService: HealthRecordService,
    private route: ActivatedRoute,
    private organizationUnitService: OrganizationUnitService
  ) {
    this.userProfileSubscription$ = null;
    this.user = null;
    this.diagnosis = new Array();
    this.userDiagnosis = new Array();
    this.selectedUserDiagnosis = new Array();
  }

  ngOnInit(): void {
    // Allow deep-linking a tab, e.g. /profile?tab=availability (from the
    // Ajanvaraus "add presence" quick-link).
    this.getOrganizationUnit$().subscribe(() => {});
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'availability') {
      this.activeTab = '1';
    } else if (tab === 'period') {
      this.activeTab = '2';
    } else if (tab === '0' || tab === '1' || tab === '2' || tab === '3') {
      this.activeTab = tab;
    }

    this.user = this.authService.getUserProfile();
    this.userService.getUser(this.user!.id).subscribe((result) => {
      this.user = result;
      this.formService.loadForm(this.formName, this.user).then((form) => {
        this.profileForm$ = form;
        // When deep-linked straight to the availability tab, the calendar was
        // initialised while its container was hidden (form still loading), so
        // it needs the same size refresh a manual tab switch triggers.
        if (this.activeTab === '1') {
          setTimeout(
            () => this.userAvailabilityComponent?.refreshCalendar(),
            0
          );
        } else if (this.activeTab === '2') {
          setTimeout(
            () => this.appointmentPeriodScheduleComponent?.refreshCalendar(),
            0
          );
        }
      });
    });
    /*
    this.userProfileSubscription$ =
      this.authService.userProfileObservable$.subscribe((result) => {
        this.user = result;
        this.formService.loadForm(this.formName, this.user).then((form) => {
          this.profileForm$ = form;
        });
      });
     */
    // Auto-load own codes on init (all codes still requires search)
    this.loading = false;
    this.loadAllUserDiagnosis();
    this.displayLanguage = defaultIcd10DisplayLanguage(
      this.utilsService.getLanguage(undefined),
      this.user?.activeOrganizationUnit?.country
    );
  }

  private getOrganizationUnit$(): Observable<unknown> {
    this.user = this.authService.getUserProfile()!;
    const organizationUnit = this.user!.activeOrganizationUnit;
    if (organizationUnit?.webScheduleFilterType) {
      if (organizationUnit?.id) {
        return this.organizationUnitService
          .getBasicUnitDataById(organizationUnit.id)
          .pipe(
            tap((res) => {
              if (res.webScheduleFilterType) {
                this.filterType = res.webScheduleFilterType;
                // this.filterType = 'speciality'; // Testing only
              }
            })
          );
      }
    }
    return of(null);
  }

  /** True when a saved favourite's code is deprecated (expired/non-leaf). */
  isDeprecated(row: ICD10): boolean {
    return isIcd10Deprecated(row);
  }

  /** Long-name text for the current FI/SV/EN/LT switch, with FI fallback. */
  longNameFor(row: ICD10): string {
    return pickIcd10LongName(row, this.displayLanguage);
  }

  /** FI shows Short name + Long name; SV/EN show one combined column. */
  showShortNameColumn(): boolean {
    return icd10ShowShortNameColumn(this.displayLanguage);
  }

  /** i18n key for the (single, in SV/EN) name column's header. */
  nameColumnHeaderKey(): string {
    return icd10NameColumnHeaderKey(this.displayLanguage);
  }

  onTabChange(value: any): void {
    this.activeTab = `${value}`;
    if (this.activeTab === '1') {
      if (this.userAvailabilityComponent) {
        this.userAvailabilityComponent.refreshCalendar();
      }
    }
    // Calendar was initialised while its tab was hidden (0 size); refresh on show.
    if (this.activeTab === '2') {
      this.appointmentPeriodScheduleComponent?.refreshCalendar();
    }
  }

  onRowSelect(event: any) {
    let icd10Item = {} as ICD10;
    icd10Item.codeId = event.data.codeId;
    icd10Item.shortName = event.data.shortName;
    icd10Item.longName = event.data.longName;
    icd10Item.status = event.data.status;
    let currentUser = this.authService.getUserProfile();
    icd10Item.userId = currentUser!.id;

    this.healthRecordService.saveUserDiagnosis(icd10Item).subscribe(
      (res) => {
        // The save response is the raw USER_ICD10_T row - it doesn't carry
        // leafNode/beginningDate/expiringDate/longNameEn/longNameSv/
        // longNameLt (those only come from the widened USER_ICD10_V join
        // / LongNameLt column). Without them,
        // isDeprecated() would treat this freshly-added, already validated
        // favourite as deprecated until the next full reload. Carry them
        // over from the search result row instead.
        const savedWithValidity: ICD10 = {
          ...res,
          leafNode: event.data.leafNode,
          beginningDate: event.data.beginningDate,
          expiringDate: event.data.expiringDate,
          longNameEn: event.data.longNameEn,
          longNameSv: event.data.longNameSv,
          longNameLt: event.data.longNameLt
        };
        // Add to both the displayed list and the master list
        this.userDiagnosis.push(savedWithValidity);
        this.allUserDiagnosisData.push(savedWithValidity);
        this.messageService.add({
          severity: 'info',
          summary: this.translateService.instant(
            'healthrecords.healthrecord.diagnosis-row-select'
          ),
          detail:
            event.data.codeId +
            ', ' +
            pickIcd10DisplayName(event.data, this.displayLanguage)
        });
      },
      (error: Error) => {
        this.utilsService.showError(error.message);
      }
    );
  }

  deleteSelectedProducts() {
    const message = this.translateService.instant(
      'healthrecords.healthrecord.user-diagnose-delete-confirm-message'
    );
    const header = this.translateService.instant(
      'healthrecords.healthrecord.delete-confirm-message'
    );
    const summary = this.translateService.instant(
      'healthrecords.healthrecord.successful-delete-summary'
    );
    const detail = this.translateService.instant(
      'healthrecords.healthrecord.successful-delete-detail'
    );
    this.confirmationService.confirm({
      message: message,
      header: header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.button.yes'),
      rejectLabel: this.translateService.instant('common.button.no'),
      accept: () => {
        this.healthRecordService
          .deleteUserDiagnosis(this.selectedProducts!)
          .subscribe(
            (res) => {
              // Remove from both displayed list and master list
              this.userDiagnosis = this.userDiagnosis.filter(
                (val) => !this.selectedProducts?.includes(val)
              );
              this.allUserDiagnosisData = this.allUserDiagnosisData.filter(
                (val) => !this.selectedProducts?.includes(val)
              );
              this.selectedProducts = null;
              this.messageService.add({
                severity: 'success',
                summary: summary,
                detail: detail,
                life: 3000
              });
            },
            (error: Error) => {
              this.utilsService.showError(error.message);
            }
          );
      }
    });
  }

  // Search term properties
  allDiagnosisSearchTerm: string = '';
  userDiagnosisSearchTerm: string = '';

  // All user diagnosis (unfiltered) for client-side filtering
  private allUserDiagnosisData: Array<ICD10> = [];

  // Load all own codes on init
  loadAllUserDiagnosis() {
    this.userDiagnosisLoading = true;
    let currentUser = this.authService.getUserProfile();
    const searchEvent = this.createSearchEvent('');
    searchEvent.userId = currentUser!.id;

    this.healthRecordService
      .userDiagnosisSearch({ lazyEvent: searchEvent })
      .then((res) => {
        this.allUserDiagnosisData = res;
        this.userDiagnosis = [...this.allUserDiagnosisData];
        this.userDiagnosisLoading = false;
      })
      .catch(() => {
        this.allUserDiagnosisData = [];
        this.userDiagnosis = [];
        this.userDiagnosisLoading = false;
      });
  }

  // Search event handlers - filter client-side for own codes, server-side for all codes
  onAllDiagnosisSearch(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.allDiagnosisSearchTerm = searchTerm;
    if (searchTerm.length >= 1) {
      this.searchDiagnosisByTerm(searchTerm);
    } else {
      this.diagnosis = [];
    }
  }

  onUserDiagnosisSearch(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.userDiagnosisSearchTerm = inputValue;
    const searchTerm = inputValue.toLowerCase();
    if (searchTerm) {
      this.userDiagnosis = this.allUserDiagnosisData.filter(
        (d) =>
          d.codeId?.toLowerCase().includes(searchTerm) ||
          d.shortName?.toLowerCase().includes(searchTerm) ||
          d.longName?.toLowerCase().includes(searchTerm)
      );
    } else {
      this.userDiagnosis = [...this.allUserDiagnosisData];
    }
  }

  private createSearchEvent(searchTerm: string): any {
    return {
      first: 0,
      rows: 1000,
      filters: {
        codeId: { value: searchTerm, matchMode: 'contains' },
        shortName: { value: '', matchMode: 'contains' },
        longName: { value: '', matchMode: 'contains' }
      },
      globalFilter: searchTerm,
      multiSortMeta: null,
      sortField: 'codeId',
      sortOrder: 1
    };
  }

  searchDiagnosisByTerm(searchTerm: string) {
    this.loading = true;
    const searchEvent = this.createSearchEvent(searchTerm);

    this.healthRecordService
      .diagnosisSearch({ lazyEvent: searchEvent })
      .then((res) => {
        this.diagnosis = res;
        this.loading = false;
      })
      .catch(() => {
        this.diagnosis = [];
        this.loading = false;
      });
  }

  searchUserDiagnosisByTerm(searchTerm: string) {
    this.userDiagnosisLoading = true;
    let currentUser = this.authService.getUserProfile();
    const searchEvent = this.createSearchEvent(searchTerm);
    searchEvent.userId = currentUser!.id;

    this.healthRecordService
      .userDiagnosisSearch({ lazyEvent: searchEvent })
      .then((res) => {
        this.userDiagnosis = res;
        this.userDiagnosisLoading = false;
      })
      .catch(() => {
        this.userDiagnosis = [];
        this.userDiagnosisLoading = false;
      });
  }

  // Keep old methods for backward compatibility but they won't be called
  searchDiagnosis(event: any, field: string) {
    // Deprecated - use searchDiagnosisByTerm instead
  }

  searchUserDiagnosis(event: any, field: string) {
    // Deprecated - use searchUserDiagnosisByTerm instead
  }

  ngOnDestroy(): void {
    if (this.userProfileSubscription$) {
      this.userProfileSubscription$.unsubscribe();
    }
  }

  submit(userData: User): void {
    const profile = this.authService.getUserProfile();

    this.userService.saveUserProfile(userData).subscribe({
      next: (result) => {
        if (result.token) {
          this.authService.setUserProfile(result.token);
          if (profile!.language !== userData.language) {
            this.changeLanguage(userData);
          }
        }
        const msg = this.translateService.instant('user.messages.save-success');
        this.utilsService.showMessage(msg);
      },
      error: (error) => {
        this.utilsService.showError(error.message);
      }
    });
  }

  /**
   * Change current language if user has changed the language setting.
   * @param user
   */
  changeLanguage(user: User): void {
    this.localeService.setLanguage(user.language);
    window.location.reload();
  }
}
